// «PENDIENTES DE ENVIAR» · DIRECTRIZ-PWA F2 (11 oct 2026, DISENO-F2 §8).
//
// Una sola lista de todo lo que esta persona guardó en el teléfono y falta
// por llegar a TEGO, más lo que el servidor no aceptó. Lee las siete
// fuentes por el registro `pendientes.js`, sin tocar ninguna cola.
//
// LO QUE NO HAY, A PROPÓSITO: «Reintentar» para lo que está por revisar.
// Reintentar lo que el servidor rechazó por regla daría el mismo rechazo;
// lo que hay es «Abrir en …» para corregirlo con señal y «Descartar» con la
// consecuencia escrita. Y nada se borra solo: un elemento con diez intentos
// sigue aquí y lo dice.
//
// A 320 px: una columna, tarjetas `w-full`, botones de 44 px apilados, el
// motivo del servidor en un párrafo con `break-words`, nada fijo.
import { useCallback, useEffect, useState } from 'react';
import {
  CloudOff, CheckCircle2, Loader2, Send, Wifi, WifiOff, Users, AlertTriangle, ChevronRight,
} from 'lucide-react';
import Ayuda from '../../ui/Ayuda';
import ModalConfirmar from '../../ui/ModalConfirmar';
import Motivo from '../../experiencia/Motivo';
import { useAuth } from '../../lib/contextoAuth';
import { useEnLinea } from '../../lib/useEnLinea';
import { horaDe } from '../../lib/conCopia';
import {
  censoDePendientes, enviarTodo, suscribirCambios, reclamarSinDueno, NOMBRE_DE_TIPO, INTENTOS_PARA_AVISAR,
  PENDIENTE, ENVIANDO, POR_REVISAR, ENVIADA, PARA_TERMINAR,
} from './pendientes';

const ORDEN_DE_ENVIO = ['movimiento', 'conteo', 'recepcion', 'traslado', 'traslado_recibir', 'lectura', 'venta', 'captura'];
const BOTON = 'inline-flex min-h-11 items-center justify-center gap-1.5 rounded-lg px-4 text-sm font-semibold '
  + 'focus:outline-none focus:ring-2 focus:ring-[var(--color-marca)] disabled:opacity-50';
const PRIMARIO = `${BOTON} bg-[var(--color-marca)] text-white hover:bg-[var(--color-marca-fuerte)]`;
const SECUNDARIO = `${BOTON} border border-gray-300 bg-white text-gray-800 hover:bg-gray-50`;
const PELIGRO = `${BOTON} border border-red-300 bg-white text-red-700 hover:bg-red-50`;

function Chip({ tipo }) {
  return (
    <span className="inline-block rounded bg-gray-100 px-2 py-0.5 text-xs font-semibold uppercase tracking-wide text-gray-700">
      {NOMBRE_DE_TIPO[tipo] || tipo}
    </span>
  );
}

function Tarjeta({ tono = 'gris', children }) {
  const clases = {
    ambar: 'border-amber-300 bg-amber-50',
    verde: 'border-green-200 bg-green-50',
    gris: 'border-gray-200 bg-white',
  }[tono];
  return <li className={`w-full rounded-xl border p-4 space-y-2 ${clases}`}>{children}</li>;
}

export default function PendientesDeEnviar({ irA }) {
  const { usuario, tieneNivel } = useAuth();
  const enLinea = useEnLinea();
  const [censo, setCenso] = useState(null);
  const [error, setError] = useState(null);
  const [tardando, setTardando] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [enviandoClave, setEnviandoClave] = useState(null);
  const [confirmando, setConfirmando] = useState(null);
  const [reclamando, setReclamando] = useState(false);
  const [aviso, setAviso] = useState(null);

  const cargar = useCallback(() => censoDePendientes(usuario)
    .then((c) => { setCenso(c); setError(null); })
    .catch(() => setError('Este navegador no deja guardar nada sin señal (almacenamiento bloqueado o lleno). '
      + 'Qué revisar: que no sea una ventana privada y que el teléfono tenga espacio.')), [usuario]);

  useEffect(() => {
    let vivo = true;
    const t = setTimeout(() => { if (vivo) setTardando(true); }, 300);
    censoDePendientes(usuario)
      .then((c) => { if (vivo) { setCenso(c); setError(null); } })
      .catch(() => { if (vivo) setError('Este navegador no deja guardar nada sin señal (almacenamiento bloqueado o lleno). '
        + 'Qué revisar: que no sea una ventana privada y que el teléfono tenga espacio.'); })
      .finally(() => clearTimeout(t));
    const dejar = suscribirCambios(() => { if (vivo) cargar(); });
    return () => { vivo = false; clearTimeout(t); dejar(); };
  }, [usuario, cargar]);

  const elementos = censo?.elementos || [];
  const porRevisar = elementos.filter((e) => e.estado === POR_REVISAR);
  const pendientes = elementos
    .filter((e) => e.estado === PENDIENTE || e.estado === ENVIANDO || e.estado === PARA_TERMINAR)
    .sort((a, b) => ORDEN_DE_ENVIO.indexOf(a.tipo) - ORDEN_DE_ENVIO.indexOf(b.tipo) || a.hora - b.hora);
  const enviadas = elementos.filter((e) => e.estado === ENVIADA);
  const porEnviar = pendientes.filter((e) => e.estado !== PARA_TERMINAR).length;
  const otraCuenta = censo?.otraCuenta || 0;
  const sinDueno = censo?.sinDueno || 0;
  const deOtra = otraCuenta - sinDueno;
  const vacio = censo !== null && elementos.length === 0 && otraCuenta === 0;

  async function enviarAhoraTodo() {
    setEnviando(true);
    setAviso(null);
    try { await enviarTodo(usuario); } finally { setEnviando(false); await cargar(); }
  }
  async function enviarAhoraUno(e) {
    setEnviandoClave(e.clave);
    setAviso(null);
    try {
      if (typeof e.enviarAhora === 'function') await e.enviarAhora();
      else await enviarTodo(usuario);
    } finally { setEnviandoClave(null); await cargar(); }
  }
  async function descartar(e) {
    // Entre abrir la confirmación y confirmar pudo empezar un envío solo:
    // lo que ya va en vuelo no se descarta (P1-2 de AUDITORIA-F2).
    const ahora = await censoDePendientes(usuario).catch(() => null);
    const vigente = ahora?.elementos.find((x) => x.clave === e.clave);
    if (vigente?.estado === ENVIANDO) {
      setConfirmando(null);
      setAviso('No se descartó: se estaba enviando. Mira cómo terminó en esta lista.');
      await cargar();
      return;
    }
    if (e.descartar) await e.descartar();
    else if (e.quitar) await e.quitar();
    setConfirmando(null);
    setAviso('Descartado.');
    await cargar();
  }
  async function reclamar() {
    const n = await reclamarSinDueno(usuario);
    setReclamando(false);
    setAviso(n === 1 ? '1 pendiente pasó a tu cuenta.' : `${n} pendientes pasaron a tu cuenta.`);
    await cargar();
  }
  const abrir = (e) => irA?.(e.abrir.modulo, e.abrir.contexto || {});

  const motivoEnviar = !enLinea
    ? 'Sin señal no hay a dónde enviar. Se intentará solo al volver.'
    : porEnviar === 0 ? 'No hay nada que enviar.' : '';

  return (
    <div className="mx-auto w-full max-w-prose space-y-4">
      <div className="flex items-start gap-3">
        <CloudOff className="h-7 w-7 shrink-0 text-[var(--color-marca)]" aria-hidden="true" />
        <div className="min-w-0">
          <h1 className="text-xl font-semibold text-gray-900">Pendientes de enviar</h1>
          <p className="text-base text-gray-600">Lo que hiciste sin señal y falta por llegar a TEGO, y lo que el servidor no aceptó.</p>
        </div>
      </div>
      <Ayuda id="pwa.pendientes.pantalla" />

      <section className="rounded-xl border border-gray-200 bg-white p-4 space-y-3">
        <p role="status" className="flex items-start gap-2 text-base">
          {enLinea
            ? <Wifi className="mt-0.5 h-5 w-5 shrink-0 text-green-700" aria-hidden="true" />
            : <WifiOff className="mt-0.5 h-5 w-5 shrink-0 text-red-600" aria-hidden="true" />}
          <span className="min-w-0">
            {!enLinea && 'Sin señal: se enviarán solos al volver.'}
            {enLinea && porEnviar > 0 && (enviando ? 'Con señal: enviando…' : `Con señal: ${porEnviar === 1 ? '1 pendiente' : `${porEnviar} pendientes`} por enviar.`)}
            {enLinea && porEnviar === 0 && 'Con señal: nada que enviar.'}
          </span>
        </p>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <button type="button" onClick={enviarAhoraTodo} disabled={!!motivoEnviar || enviando}
                  aria-busy={enviando} aria-describedby={motivoEnviar ? 'pendientes-enviar-motivo' : undefined}
                  className={`${PRIMARIO} w-full sm:w-auto`}>
            {enviando ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <Send className="h-4 w-4" aria-hidden="true" />}
            {enviando ? 'Enviando…' : 'Enviar ahora'}
          </button>
          <Motivo id="pendientes-enviar-motivo" cuando={!!motivoEnviar}>{motivoEnviar}</Motivo>
        </div>
        <Ayuda id="pwa.pendientes.enviar_ahora" />
        {aviso && <p role="status" className="text-sm font-semibold text-green-800">{aviso}</p>}
      </section>

      {error && (
        <div role="alert" className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800 break-words">{error}</div>
      )}

      {censo === null && !error && tardando && (
        <p role="status" className="flex items-center gap-2 text-sm text-gray-600">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Mirando qué hay guardado en este teléfono…
        </p>
      )}

      {vacio && (
        <section className="rounded-xl border border-gray-200 bg-white p-6 text-center">
          <CheckCircle2 className="mx-auto mb-2 h-10 w-10 text-green-700" aria-hidden="true" />
          <p className="text-base font-semibold text-gray-900">No hay nada pendiente.</p>
          <p className="text-base text-gray-800">Todo lo que hiciste ya está en TEGO.</p>
          <p className="mt-2 text-sm text-gray-600">Cuando trabajes sin señal, lo que guardes aparecerá aquí hasta que se envíe.</p>
        </section>
      )}

      {porRevisar.length > 0 && (
        <section className="space-y-2">
          <h2 className="flex items-center gap-2 text-base font-semibold text-amber-900">
            <AlertTriangle className="h-5 w-5 shrink-0" aria-hidden="true" /> Por revisar
          </h2>
          <Ayuda id="pwa.pendientes.por_revisar" />
          <ul className="space-y-2">
            {porRevisar.map((e) => (
              <Tarjeta key={e.clave} tono="ambar">
                <div className="flex flex-wrap items-center gap-2"><Chip tipo={e.tipo} /><span className="text-xs text-gray-600">Guardado a las {horaDe(e.hora)}{e.rechazadaEn ? ` · Rechazado a las ${horaDe(e.rechazadaEn)}` : ''}</span></div>
                <p className="min-w-0 break-words text-base font-semibold text-gray-900">{e.titulo}</p>
                {e.detalle && <p className="text-sm text-gray-700 break-words">{e.detalle}</p>}
                <p className="text-sm text-amber-900 break-words"><strong>Qué dijo TEGO:</strong> {e.motivo || 'Sin detalle del servidor.'}</p>
                {e.queHacer && <p className="text-sm text-gray-800 break-words"><strong>Qué hacer:</strong> {e.queHacer}</p>}
                <div className="flex flex-col gap-2 sm:flex-row">
                  {e.abrir && <button type="button" onClick={() => abrir(e)} className={SECUNDARIO}>{e.abrir.etiqueta} <ChevronRight className="h-4 w-4" aria-hidden="true" /></button>}
                  {(e.descartar || e.quitar) && <button type="button" onClick={() => setConfirmando(e)} className={PELIGRO}>Descartar</button>}
                </div>
              </Tarjeta>
            ))}
          </ul>
        </section>
      )}

      {pendientes.length > 0 && (
        <section className="space-y-2">
          <h2 className="text-base font-semibold text-gray-900">Pendientes de enviar</h2>
          <ul className="space-y-2">
            {pendientes.map((e) => {
              const enviandoEste = enviandoClave === e.clave || e.estado === ENVIANDO;
              return (
                <Tarjeta key={e.clave}>
                  <div className="flex flex-wrap items-center gap-2">
                    <Chip tipo={e.tipo} />
                    {e.estado === PARA_TERMINAR && <span className="rounded bg-blue-100 px-2 py-0.5 text-xs font-semibold text-blue-800">Para terminar</span>}
                    {enviandoEste && <span role="status" className="inline-flex items-center gap-1 text-xs font-semibold text-[var(--color-marca)]"><Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> Enviando…</span>}
                  </div>
                  <p className="min-w-0 break-words text-base font-semibold text-gray-900">{e.titulo}</p>
                  {e.detalle && <p className="text-sm text-gray-700 break-words">{e.detalle}</p>}
                  <p className="text-sm text-gray-600">Guardado en este teléfono a las {horaDe(e.hora)}</p>
                  {e.espera && <p className="text-sm text-amber-900 break-words">{e.espera}</p>}
                  {e.intentos >= INTENTOS_PARA_AVISAR && (
                    <p className="text-sm text-amber-900 break-words">
                      TEGO lleva {e.intentos} intentos sin respuesta del servidor. Revisa que tengas señal de verdad; si sigue igual, avisa a quien cuide el servidor.
                    </p>
                  )}
                  <div className="flex flex-col gap-2 sm:flex-row">
                    {e.estado !== PARA_TERMINAR && e.enviarAhora && (
                      <button type="button" onClick={() => enviarAhoraUno(e)} disabled={!enLinea || enviandoEste}
                              aria-describedby={!enLinea ? 'pendientes-enviar-motivo' : undefined} className={SECUNDARIO}>
                        <Send className="h-4 w-4" aria-hidden="true" /> Enviar ahora
                      </button>
                    )}
                    {e.estado === PARA_TERMINAR && e.abrir && <button type="button" onClick={() => abrir(e)} className={PRIMARIO}>{e.abrir.etiqueta}</button>}
                    {(e.descartar || e.quitar) && (
                      <button type="button" disabled={enviandoEste}
                              aria-describedby={enviandoEste ? `descartar-motivo-${e.clave}` : undefined}
                              onClick={() => setConfirmando(e)} className={PELIGRO}>
                        {e.quitar && !e.descartar ? 'Quitar' : 'Descartar'}
                      </button>
                    )}
                  </div>
                  <Motivo id={`descartar-motivo-${e.clave}`} cuando={enviandoEste && !!(e.descartar || e.quitar)}>
                    Se está enviando: espera a que termine para decidir si lo descartas.
                  </Motivo>
                </Tarjeta>
              );
            })}
          </ul>
        </section>
      )}

      {enviadas.length > 0 && (
        <details className="rounded-xl border border-green-200 bg-green-50 p-4">
          <summary className="flex min-h-11 cursor-pointer items-center gap-2 text-base font-medium text-green-900">
            <CheckCircle2 className="h-5 w-5 shrink-0" aria-hidden="true" /> Enviadas hoy ({enviadas.length})
          </summary>
          <ul className="mt-2 space-y-1">
            {enviadas.map((e) => (
              <li key={e.clave} className="text-sm text-green-900 break-words">
                {e.titulo} — Enviada a las {horaDe(e.enviadaEn)}{e.folio ? ` · folio ${e.folio}` : ''}
              </li>
            ))}
          </ul>
        </details>
      )}

      {otraCuenta > 0 && (
        <section className="rounded-xl border border-gray-200 bg-white p-4 space-y-2">
          <p className="flex items-start gap-2 text-base text-gray-800">
            <Users className="mt-0.5 h-5 w-5 shrink-0 text-gray-500" aria-hidden="true" />
            <span className="min-w-0">
              {deOtra > 0 && <>{deOtra === 1 ? '1 pendiente guardado' : `${deOtra} pendientes guardados`} en este teléfono por otra persona. Para enviarlos hay que entrar con esa cuenta. </>}
              {sinDueno > 0 && <>{sinDueno === 1 ? '1 pendiente se guardó' : `${sinDueno} pendientes se guardaron`} en este teléfono antes de saber de qué cuenta {sinDueno === 1 ? 'es' : 'son'}: no se ven ni se envían desde ninguna. Si {sinDueno === 1 ? 'es tuyo, pásalo' : 'son tuyos, pásalos'} a tu cuenta.</>}
            </span>
          </p>
          <Ayuda id="pwa.pendientes.otra_cuenta" />
          {sinDueno > 0 && (
            <button type="button" onClick={() => setReclamando(true)} className={`${SECUNDARIO} w-full sm:w-auto`}>
              <Users className="h-4 w-4" aria-hidden="true" /> Son míos: pasarlos a mi cuenta
            </button>
          )}
        </section>
      )}

      {tieneNivel?.('ADMIN') && (
        <button type="button" onClick={() => irA?.('cobrar-sin-senal')} className="inline-flex min-h-11 items-center gap-1 text-sm font-semibold text-[var(--color-marca)] underline">
          Cobrar sin señal: ajuste de la empresa <ChevronRight className="h-4 w-4" aria-hidden="true" />
        </button>
      )}

      {reclamando && (
        <ModalConfirmar
          titulo="¿Son tuyos los pendientes sin dueño conocido?"
          descripcion={`${sinDueno === 1 ? 'Ese pendiente pasará' : `Esos ${sinDueno} pendientes pasarán`} a tu cuenta y se enviarán con tu nombre en TEGO. Hazlo solo si los guardaste tú en este teléfono.`}
          etiquetaConfirmar="Sí, son míos"
          extra={<Ayuda id="pwa.pendientes.reclamar" />}
          onConfirmar={reclamar}
          onCerrar={() => setReclamando(false)}
        />
      )}

      {confirmando && (
        <ModalConfirmar
          titulo={`¿Descartar «${confirmando.titulo}»?`}
          descripcion={`Se borra de este teléfono y no se enviará nunca. ${confirmando.consecuencia || ''}`.trim()}
          etiquetaConfirmar="Sí, descartar"
          peligro
          extra={<Ayuda id="pwa.pendientes.descartar" />}
          onConfirmar={() => descartar(confirmando)}
          onCerrar={() => setConfirmando(null)}
        />
      )}
    </div>
  );
}
